import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { MatSnackBar as MatSnackBar, MatSnackBarRef as MatSnackBarRef, SimpleSnackBar as SimpleSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';

@Injectable()
export class UserInactivityService {
  private noUserInputTimeoutId: any;
  private userActivityTimeoutId: any;
  private userActivityHandler = this.handleUserAction.bind(this);
  private snackBarRef: MatSnackBarRef<SimpleSnackBar>;
  private snackBarDisplayTimeInMiliseconds = 60000; // 1 minute
  private refreshTokenHandler = this.handleRefreshAccess.bind(this);

  userActivity = new Subject<boolean>();

  constructor(
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {}

  setupUserActivityListener(): void {
    this.addUserActivityListener();
    this.handleUserAction();
  }

  clearEventListeners(): void {
    window.removeEventListener('click', this.refreshTokenHandler);
    window.removeEventListener('click', this.userActivityHandler);
  }

  addUserActivityListener(): void {
    window.addEventListener('click', this.userActivityHandler);
  }

  clearTimeouts(): void {
    clearTimeout(this.noUserInputTimeoutId);
    clearTimeout(this.userActivityTimeoutId);
  }

  private setupTimeoutSnackBar(): void {
    let description: string;
    let actionText: string;
    this.translateService
      .get('TIMEOUT-SNACKBAR.DESCRIPTION')
      .toPromise()
      .then(t => (description = t))
      .then(() =>
        this.translateService
          .get('TIMEOUT-SNACKBAR.ACTION')
          .toPromise()
          .then(t => (actionText = t))
          .then(() => {
            this.snackBarRef = this.snackBar.open(description, actionText, {
              duration: this.snackBarDisplayTimeInMiliseconds
            });
            this.snackBarRef
              .onAction()
              .subscribe(() => this.handleRefreshAccess());
          })
      );
  }

  private performTimeoutActions(isTimeout: boolean): void {
    this.clearTimeoutBackgroundProcesses();
    if (!!this.snackBar) {
      this.snackBar.dismiss();
    }
    if (isTimeout) {
      this.userActivity.next(true);
    }
  }

  private handleUserAction(): void {
    clearTimeout(this.userActivityTimeoutId);
    this.setUserActivityTimeout();
  }

  private setUserActivityTimeout(): void {
    this.userActivityTimeoutId = setTimeout(() => {
      this.setupSnackBar();
    }, parseInt(environment.userSessionValidityMs));
  }

  private setupSnackBar(): void {
    this.noUserInputTimeoutId = setTimeout(
      () => this.performTimeoutActions(true),
      this.snackBarDisplayTimeInMiliseconds
    );
    this.setupTimeoutSnackBar();
    this.clearEventListeners();
    window.addEventListener('click', this.refreshTokenHandler);
  }

  private clearTimeoutBackgroundProcesses(): void {
    if (!!this.noUserInputTimeoutId) {
      clearTimeout(this.noUserInputTimeoutId);
    }
    this.clearEventListeners();
  }

  private handleRefreshAccess(): void {
    this.performTimeoutActions(false);
    this.handleUserAction();
    this.userActivity.next(false);
    this.clearEventListeners();
    this.addUserActivityListener();
  }
}
